/**
 * EventsSelectionDock
 *
 * Floating dock shown over the events table whenever at least one event is
 * selected (GRAIN-1488). It reports the selection count and offers "Select all
 * events matching date", which selects every event for the active date filter,
 * bypassing pagination.
 *
 * Also morphs in place through the generate-documents mutation lifecycle
 * (`loading → queued → error`) so the whole bottom-of-table feedback system
 * is ONE mounted pill — no swapping elements. Follows the same pattern as
 * `BulkActivateDeactivateBar` in the products table.
 *
 * ## State machine
 *
 * | mode      | content                                                   |
 * |-----------|-----------------------------------------------------------|
 * | `selected`| N events selected + select-all-matching-date + Generate   |
 * | `loading` | spinner + "Generating documents"                          |
 * | `queued`  | check + success copy + dismiss × + auto-dismiss timer     |
 * | `error`   | red × + error copy + dismiss × (no auto-dismiss)          |
 *
 * Reuses the shared bulk-action chrome from `BulkActionBar` (the same shell
 * the products bulk bars and capacity bulk edit use), so it stays visually and
 * behaviourally consistent across the portal.
 */

// Icons
import { UNDERLINE_STATIC } from '@/components/ui/link-underline';
import { cn } from '@/lib/utils';
import { LoadingSpinnerIcon } from '@icons/LoadingSpinnerIcon';
import { CancelTagIcon } from '@icons/CancelTagIcon';
import { CheckCircleIcon } from '@icons/CheckCircleIcon';
import { CloseIcon } from '@icons/CloseIcon';

// Shared chrome
import {
  BulkActionBar,
  BulkActionButton,
  CountedText,
  DismissProgressBar,
  FadeIn,
  SelectAllMatchingToggle,
} from '@/components/ui/bulk-action-bar';

// Hooks and utilities
import { useTranslation } from '@hooks/useTranslation';


const LOADING_ICON = <LoadingSpinnerIcon className="size-5 shrink-0 text-off-black/60" />;
const QUEUED_ICON = <CheckCircleIcon className="size-5 shrink-0 text-yield-positive" aria-hidden="true" />;
const ERROR_ICON = <CancelTagIcon className="shrink-0 text-chilli-red" />;

export type EventsSelectionDockMode = 'selected' | 'loading' | 'queued' | 'error';

export interface EventsSelectionDockProps {
  /** Events currently selected (manual count, or the date total in all-matching mode). */
  selectedCount: number;
  /** Whether a date filter is in effect, so "select all matching date" is possible. */
  canSelectAllMatchingDate: boolean;
  /** Whether every event for the date is already selected. */
  allMatchingDate: boolean;
  /** True while the full matching-date id set is being resolved — shows a spinner
   *  in place of the checkbox (instead of a disabled "not-allowed" state). */
  selectingAllMatchingDate?: boolean;
  onSelectAllMatchingDate: () => void;
  /** Hidden when omitted. */
  onGenerateDocuments?: () => void;
  /** True when no selected event is published, so there is nothing to generate. */
  generateDocumentsDisabled?: boolean;
  /** Current mode — drives which content is rendered inside the single pill.
   *  Defaults to `'selected'`. */
  mode?: EventsSelectionDockMode;
  /** Fires when the user explicitly clicks the × dismiss button (queued or error mode). */
  onGenerateDismiss?: () => void;
  /** Fires when the queued auto-dismiss timer completes. */
  onGenerateAutoDismiss?: () => void;
  /** Fires when the underlined "Queued" word is clicked — opens the Activity Centre. */
  onQueuedLinkClick?: () => void;
}

export function EventsSelectionDock({
  selectedCount,
  canSelectAllMatchingDate,
  allMatchingDate,
  selectingAllMatchingDate = false,
  onSelectAllMatchingDate,
  onGenerateDocuments,
  generateDocumentsDisabled = false,
  mode = 'selected',
  onGenerateDismiss,
  onGenerateAutoDismiss,
  onQueuedLinkClick,
}: EventsSelectionDockProps) {
  const { t } = useTranslation();

  // The success copy is split across two keys so the interactive lead word
  // ("Queued" — an underlined link to the Activity Centre) is locale-independent
  // of the plain remainder (": Event order documents").
  const queuedLinkWord = t('download_modal.generate_success_link');
  const queuedRemainder = t('download_modal.generate_success_rest');

  const ariaLabel =
    mode === 'selected' ? t('selection.aria_label')
    : mode === 'loading' ? t('download_modal.generate_loading')
    : mode === 'queued' ? `${queuedLinkWord}${queuedRemainder}`
    : t('download_modal.generate_error');

  return (
    <BulkActionBar ariaLabel={ariaLabel} className="gap-6">
      <FadeIn keyId={mode}>
        {mode === 'selected' && (
          <>
            <CountedText count={selectedCount} template={t('selection.count_label')} />
            <SelectAllMatchingToggle
              checked={allMatchingDate}
              disabled={!canSelectAllMatchingDate}
              loading={selectingAllMatchingDate}
              onToggle={onSelectAllMatchingDate}
              label={t('selection.select_all_matching_date')}
              disabledReason={t('selection.select_all_matching_date_disabled_reason')}
              className="ml-6"
            />
            {onGenerateDocuments && (
              <BulkActionButton
                onClick={onGenerateDocuments}
                disabled={generateDocumentsDisabled}
                disabledReason={t('selection.generate_documents_disabled_reason')}
                className="ml-6"
              >
                {t('selection.generate_documents')}
              </BulkActionButton>
            )}
          </>
        )}

        {mode === 'loading' && (
          <span className="flex items-center gap-2">
            {LOADING_ICON}
            <span className="whitespace-nowrap text-sm font-semibold leading-[1.5] text-off-black">
              {t('download_modal.generate_loading')}
            </span>
          </span>
        )}

        {mode === 'queued' && (
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold leading-[1.5] text-off-black">
              {QUEUED_ICON}
              <span className="inline-flex items-center">
                <button
                  type="button"
                  onClick={onQueuedLinkClick}
                  className={cn('cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring', UNDERLINE_STATIC)}
                >
                  {queuedLinkWord}
                </button>
                {queuedRemainder && <span>{queuedRemainder}</span>}
              </span>
            </span>
            <button
              type="button"
              onClick={onGenerateDismiss}
              aria-label={t('bulk_bar.dismiss')}
              className="flex size-5 shrink-0 items-center justify-center rounded-xs p-1 text-off-black hover:bg-dinnerplate-white-light focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <CloseIcon className="size-3" />
            </button>
          </div>
        )}

        {mode === 'error' && (
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-[6px]">
              {ERROR_ICON}
              <span className="whitespace-nowrap text-sm font-semibold leading-[1.5] text-off-black">
                {t('download_modal.generate_error')}
              </span>
            </span>
            <button
              type="button"
              onClick={onGenerateDismiss}
              aria-label={t('bulk_bar.dismiss')}
              className="flex size-5 shrink-0 items-center justify-center rounded-xs p-1 text-off-black hover:bg-dinnerplate-white-light focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <CloseIcon className="size-3" />
            </button>
          </div>
        )}
      </FadeIn>

      {mode === 'queued' && (
        <DismissProgressBar onComplete={onGenerateAutoDismiss} />
      )}
    </BulkActionBar>
  );
}
